import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import GemeenteCombobox from './GemeenteCombobox'
import Turnstile, { type TurnstileHandvat } from './Turnstile'
import { turnstileSitekey } from '../lib/turnstile'
import { caretNaFormatteren, formatteerDatum } from '../lib/datumveld'
import { tariefTekst } from '../lib/tarief'
import {
  FormulierAfgekeurd,
  BetalingNietGestart,
  verstuurAanvraag,
} from '../lib/api'
import {
  valideerAanvraag,
  veldvolgorde,
  type AanvraagFormulierWaarden,
  type Veldfouten,
} from '../lib/aanvraagvalidatie'

/*
 * Het aanvraagformulier uit legacy/current/index.html: dezelfde velden, dezelfde
 * labels, dezelfde hints en dezelfde indeling (FR-04, FR-05).
 *
 * Twee teksten wijken bewust af van de bestaande site, omdat die een boekingsstap
 * beloofden die v1 niet heeft (FR-10, FR-12a), en het veld "Korte toelichting" is
 * vervallen (ADR-0009). Alle drie staan hieronder toegelicht op de plek zelf.
 *
 * De velden zijn ongecontroleerd: bij het valideren en verzenden wordt het
 * formulier zelf uitgelezen. Vijftien stukjes state die precies de waarde van hun
 * invoerveld nabootsen leveren hier niets op — geen enkele waarde stuurt iets
 * anders aan op het scherm. Twee velden zijn de uitzondering, en allebei omdat de
 * waarde daar wél iets aanstuurt: geboortedatum herschrijft de invoer tijdens het
 * typen, en gemeente filtert de suggestielijst.
 */

/** Meldingen die niet aan een veld hangen, komen in de balk boven de knop. */
const nietAllesIngevuld =
  'Nog niet alles is goed ingevuld. Kijk de rood gemarkeerde velden na.'

export default function AanvraagFormulier() {
  const [zoekparameters] = useSearchParams()
  const gekozenKaarttype = zoekparameters.get('kaarttype') ?? 'B'

  const turnstile = useRef<TurnstileHandvat | null>(null)
  const turnstileToken = useRef('')

  const [fouten, setFouten] = useState<Veldfouten>({})
  const [melding, setMelding] = useState('')
  const [bezig, setBezig] = useState(false)
  const [afgerond, setAfgerond] = useState<string | null>(null)

  // Geboortedatum is het enige veld met eigen invoergedrag; de rest is
  // ongecontroleerd.
  const [geboortedatum, setGeboortedatum] = useState('')
  const geboortedatumVeld = useRef<HTMLInputElement>(null)
  const caret = useRef<number | null>(null)

  // React herstelt de cursor niet na het herschrijven van de waarde; dat doen we
  // hier, ná de render waarin de nieuwe waarde in het veld staat.
  useEffect(() => {
    if (caret.current === null) return
    geboortedatumVeld.current?.setSelectionRange(caret.current, caret.current)
    caret.current = null
  }, [geboortedatum])

  function bijDatumInvoer(gebeurtenis: React.ChangeEvent<HTMLInputElement>) {
    const veld = gebeurtenis.target
    const totCursor = veld.value.slice(
      0,
      veld.selectionStart ?? veld.value.length,
    )

    const nieuweWaarde = formatteerDatum(veld.value)
    caret.current = caretNaFormatteren(
      nieuweWaarde,
      totCursor.replace(/\D/g, '').length,
    )
    setGeboortedatum(nieuweWaarde)
  }

  function wisFout(veld: keyof AanvraagFormulierWaarden) {
    setFouten((huidig) => {
      if (!(veld in huidig)) return huidig

      const rest = { ...huidig }
      delete rest[veld]
      return rest
    })
  }

  /**
   * Haalt een fout weg zodra het veld goed ingevuld is — maar zet er geen nieuwe
   * neer terwijl er getypt wordt. Een e-mailadres is halverwege het intypen nu
   * eenmaal ongeldig, en daar een melding bij zetten is bemoeizucht.
   */
  function bijInvoer(gebeurtenis: React.FormEvent<HTMLFormElement>) {
    const veld = (gebeurtenis.target as HTMLInputElement).name
    if (!veld || !isFormulierveld(veld) || !(veld in fouten)) return

    const opnieuw = valideerAanvraag(leesWaarden(gebeurtenis.currentTarget))
    if (opnieuw[veld]) return

    wisFout(veld)
  }

  async function bijVerzenden(gebeurtenis: React.FormEvent<HTMLFormElement>) {
    gebeurtenis.preventDefault()
    if (bezig) return

    const form = gebeurtenis.currentTarget
    const waarden = leesWaarden(form)
    const gevonden = valideerAanvraag(waarden)

    if (Object.keys(gevonden).length > 0) {
      toonFouten(form, gevonden, nietAllesIngevuld)
      return
    }

    setFouten({})
    setMelding('')
    setBezig(true)

    try {
      const antwoord = await verstuurAanvraag({
        ...waarden,
        // De spamval (FR-24). Leeg bij een mens; de server beslist wat er met
        // een ingevuld veld gebeurt.
        website: leesVeld(form, 'website'),
        turnstileToken: turnstileToken.current,
      })

      // Volledige navigatie, geen router-overgang: de checkout staat bij Mollie.
      window.location.href = antwoord.checkoutUrl
    } catch (fout: unknown) {
      // Het token van Turnstile is eenmalig. Zonder een vers exemplaar wordt een
      // tweede poging afgekeurd op iets wat de bezoeker niet kan zien.
      turnstile.current?.herstart()

      if (fout instanceof FormulierAfgekeurd) {
        const perVeld: Veldfouten = {}
        const overig: string[] = []

        for (const { veld, melding: tekst } of fout.fouten) {
          if (isFormulierveld(veld)) {
            perVeld[veld] = tekst
          } else {
            overig.push(tekst)
          }
        }

        toonFouten(
          form,
          perVeld,
          overig.length > 0 ? overig.join(' ') : nietAllesIngevuld,
        )
        setBezig(false)
        return
      }

      if (fout instanceof BetalingNietGestart) {
        // De aanvraag staat vast. Het formulier laten staan zou uitnodigen tot
        // een tweede inzending, en dat is een tweede aanvraag (FR-11).
        setAfgerond(fout.melding)
        return
      }

      setMelding(
        'Het lukt nu even niet om uw aanvraag te versturen. Controleer uw ' +
          'e-mail voordat u het opnieuw probeert: mogelijk is hij toch ' +
          'aangekomen en staat de bevestiging al klaar.',
      )
      setBezig(false)
    }
  }

  function toonFouten(
    form: HTMLFormElement,
    gevonden: Veldfouten,
    tekst: string,
  ) {
    setFouten(gevonden)
    setMelding(tekst)

    // Het eerste foute veld van boven naar beneden, niet het eerste dat de
    // validator toevallig tegenkwam.
    const eerste = veldvolgorde.find((veld) => gevonden[veld])
    if (!eerste) return

    // Bij een radiogroep levert `namedItem` de hele groep; dan is het eerste
    // knopje de plek om naartoe te springen.
    const gevondenElement = form.elements.namedItem(eerste)
    const doel =
      gevondenElement instanceof RadioNodeList
        ? gevondenElement.item(0)
        : gevondenElement

    if (doel instanceof HTMLElement) doel.focus()
  }

  if (afgerond !== null) {
    return (
      <div className="formcard" role="status">
        <h2>Uw aanvraag is ontvangen</h2>
        <p className="lede">{afgerond}</p>
      </div>
    )
  }

  return (
    <form
      className="formcard reveal"
      id="aanvraagForm"
      noValidate
      onInput={bijInvoer}
      onSubmit={(e) => void bijVerzenden(e)}
    >
      <div className="form-grid">
        <div className="form-sep">Uw aanvraag</div>
        <div className="fld c3">
          <label id="lbl-kaarttype">
            Aanvraag betreft <span aria-hidden="true">*</span>
          </label>
          <div
            className="seg"
            role="radiogroup"
            aria-labelledby="lbl-kaarttype"
          >
            {(['B', 'P', 'B+P'] as const).map((waarde) => (
              <label key={waarde}>
                <input
                  type="radio"
                  name="kaarttype"
                  value={waarde}
                  defaultChecked={waarde === gekozenKaarttype}
                  required
                />
                {/* Als één tekstknoop, anders zet React er bij het prerenderen
                    een scheiding tussen en leest een schermlezer "GPK" en "B"
                    als twee woorden. */}
                <span>{`GPK-${waarde}`}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="fld c3">
          <label id="lbl-aanvraagtype">
            Type aanvraag <span aria-hidden="true">*</span>
          </label>
          <div
            className="seg seg--2"
            role="radiogroup"
            aria-labelledby="lbl-aanvraagtype"
          >
            <label>
              <input
                type="radio"
                name="aanvraagtype"
                value="eerste"
                defaultChecked
              />
              <span>Eerste aanvraag</span>
            </label>
            <label>
              <input type="radio" name="aanvraagtype" value="verlenging" />
              <span>Verlenging</span>
            </label>
          </div>
        </div>

        <div className="form-sep">Uw gegevens</div>
        <div className={fldKlasse('c2', fouten.achternaam)}>
          <label htmlFor="f-achternaam">
            Achternaam <span aria-hidden="true">*</span>
          </label>
          <input
            id="f-achternaam"
            name="achternaam"
            type="text"
            autoComplete="family-name"
            required
            aria-invalid={fouten.achternaam ? true : undefined}
            aria-describedby="e-achternaam"
          />
          <span className="err" id="e-achternaam" role="alert">
            {fouten.achternaam}
          </span>
        </div>
        <div className={fldKlasse('c1 fld--half', fouten.tussenvoegsel)}>
          {/*
            Geen "(niet verplicht)" achter dit label. Het veld staat in een
            c1-kolom van zo'n 150px; die toevoeging maakte het label twee regels
            hoog en duwde het invoerveld omlaag ten opzichte van Voorletters
            ernaast. Dat dit veld optioneel is, blijkt uit het ontbreken van de
            asterisk die alle verplichte velden wél hebben.
          */}
          <label htmlFor="f-tussen">Tussenvoegsel</label>
          <input
            id="f-tussen"
            name="tussenvoegsel"
            type="text"
            aria-invalid={fouten.tussenvoegsel ? true : undefined}
            aria-describedby="e-tussen"
          />
          <span className="err" id="e-tussen" role="alert">
            {fouten.tussenvoegsel}
          </span>
        </div>
        <div className={fldKlasse('c1 fld--half', fouten.voorletters)}>
          <label htmlFor="f-voorletters">
            Voorletters <span aria-hidden="true">*</span>
          </label>
          <input
            id="f-voorletters"
            name="voorletters"
            type="text"
            required
            aria-invalid={fouten.voorletters ? true : undefined}
            aria-describedby="e-voorletters"
          />
          <span className="err" id="e-voorletters" role="alert">
            {fouten.voorletters}
          </span>
        </div>
        <div className={fldKlasse('c2', fouten.voornaam)}>
          <label htmlFor="f-voornaam">
            Eerste voornaam voluit <span aria-hidden="true">*</span>
          </label>
          <input
            id="f-voornaam"
            name="voornaam"
            type="text"
            autoComplete="given-name"
            required
            aria-invalid={fouten.voornaam ? true : undefined}
            aria-describedby="e-voornaam"
          />
          <span className="err" id="e-voornaam" role="alert">
            {fouten.voornaam}
          </span>
        </div>
        <div className={fldKlasse('c2', fouten.geboortedatum)}>
          <label htmlFor="f-geboortedatum">
            Geboortedatum <span aria-hidden="true">*</span>
          </label>
          <input
            id="f-geboortedatum"
            name="geboortedatum"
            type="text"
            inputMode="numeric"
            placeholder="dag-maand-jaar"
            autoComplete="bday"
            required
            maxLength={10}
            ref={geboortedatumVeld}
            value={geboortedatum}
            onChange={bijDatumInvoer}
            aria-invalid={fouten.geboortedatum ? true : undefined}
            aria-describedby="e-geboortedatum h-geboortedatum"
          />
          <span className="hint" id="h-geboortedatum">
            Bijvoorbeeld: 03-05-1952. U hoeft alleen de cijfers te typen.
          </span>
          <span className="err" id="e-geboortedatum" role="alert">
            {fouten.geboortedatum}
          </span>
        </div>
        <div className={fldKlasse('c2', fouten.geslacht)}>
          <label htmlFor="f-geslacht">
            Geslacht <span aria-hidden="true">*</span>
          </label>
          <select
            id="f-geslacht"
            name="geslacht"
            required
            aria-invalid={fouten.geslacht ? true : undefined}
            aria-describedby="e-geslacht"
            defaultValue=""
          >
            <option value="">Maak een keuze</option>
            <option value="man">Man</option>
            <option value="vrouw">Vrouw</option>
            <option value="anders">Anders</option>
          </select>
          <span className="err" id="e-geslacht" role="alert">
            {fouten.geslacht}
          </span>
        </div>
        <div className={fldKlasse('c2', fouten.telefoon)}>
          <label htmlFor="f-telefoon">
            Telefoonnummer <span aria-hidden="true">*</span>
          </label>
          <input
            id="f-telefoon"
            name="telefoon"
            type="tel"
            autoComplete="tel"
            required
            aria-invalid={fouten.telefoon ? true : undefined}
            aria-describedby="e-telefoon"
          />
          <span className="err" id="e-telefoon" role="alert">
            {fouten.telefoon}
          </span>
        </div>

        <div className="form-sep">Uw adres</div>
        <div className={fldKlasse('c3', fouten.straat)}>
          <label htmlFor="f-straat">
            Straatnaam <span aria-hidden="true">*</span>
          </label>
          <input
            id="f-straat"
            name="straat"
            type="text"
            autoComplete="address-line1"
            required
            aria-invalid={fouten.straat ? true : undefined}
            aria-describedby="e-straat"
          />
          <span className="err" id="e-straat" role="alert">
            {fouten.straat}
          </span>
        </div>
        <div className={fldKlasse('c1 fld--half', fouten.huisnummer)}>
          <label htmlFor="f-huisnummer">
            Huisnummer <span aria-hidden="true">*</span>
          </label>
          <input
            id="f-huisnummer"
            name="huisnummer"
            type="text"
            inputMode="numeric"
            required
            aria-invalid={fouten.huisnummer ? true : undefined}
            aria-describedby="e-huisnummer"
          />
          <span className="err" id="e-huisnummer" role="alert">
            {fouten.huisnummer}
          </span>
        </div>
        <div className={fldKlasse('c2 fld--half', fouten.postcode)}>
          <label htmlFor="f-postcode">
            Postcode <span aria-hidden="true">*</span>
          </label>
          <input
            id="f-postcode"
            name="postcode"
            type="text"
            autoComplete="postal-code"
            placeholder="1234 AB"
            required
            aria-invalid={fouten.postcode ? true : undefined}
            aria-describedby="e-postcode"
          />
          <span className="err" id="e-postcode" role="alert">
            {fouten.postcode}
          </span>
        </div>
        <div className={fldKlasse('c2', fouten.woonplaats)}>
          <label htmlFor="f-woonplaats">
            Woonplaats <span aria-hidden="true">*</span>
          </label>
          <input
            id="f-woonplaats"
            name="woonplaats"
            type="text"
            autoComplete="address-level2"
            required
            aria-invalid={fouten.woonplaats ? true : undefined}
            aria-describedby="e-woonplaats"
          />
          <span className="err" id="e-woonplaats" role="alert">
            {fouten.woonplaats}
          </span>
        </div>
        {/*
          Het gemeenteveld draagt zijn eigen label, hint en foutmelding; de
          combobox eromheen heeft te veel eigen gedrag om hier open en bloot te
          staan. Kiezen uit de lijst gaat buiten het `input`-event om, dus de
          foutmelding wordt hier weggehaald.
        */}
        <GemeenteCombobox
          fout={fouten.gemeente}
          bijKeuze={() => wisFout('gemeente')}
        />
        <div className={fldKlasse('c2', fouten.email)}>
          <label htmlFor="f-email">
            E-mailadres <span aria-hidden="true">*</span>
          </label>
          <input
            id="f-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            aria-invalid={fouten.email ? true : undefined}
            aria-describedby="e-email h-email"
          />
          {/*
            De bestaande site belooft hier "de afspraaklink". Die bestaat niet in
            v1; wat de cliënt krijgt is de ontvangstbevestiging met een betaallink
            die geldig blijft (FR-08a, ADR-0006).
          */}
          <span className="hint" id="h-email">
            Hierop ontvangt u de bevestiging en de betaallink.
          </span>
          <span className="err" id="e-email" role="alert">
            {fouten.email}
          </span>
        </div>

        {/*
          Hier staat op de bestaande site het veld "Korte toelichting".
          ADR-0009 laat dat in v1 vervallen. De reden staat in R-02: een vrij
          tekstveld op een formulier over een medische beoordeling nodigt uit tot
          het delen van medische details, en die belanden dan onversleuteld in een
          mailbox. De waarschuwing eronder ("deel hier geen medische details")
          loste dat niet op — hij vraagt de cliënt om iets te laten dat het veld
          zelf uitlokt.
        */}

        {/*
          De spamval (FR-24): een veld dat een mens nooit ziet en een bot invult.
          aria-hidden en tabindex houden hem weg bij schermlezers en de
          toetsenbordvolgorde. De controle die telt staat in AanvraagFunction; dit
          is alleen de val zelf.

          Het buiten beeld zetten gebeurt in `.spamval` in site.css en niet hier
          in een style-attribuut: de CSP blokkeert inline styles, waardoor het
          veld gewoon zichtbaar werd.
        */}
        <div className="fld spamval" aria-hidden="true">
          <label htmlFor="f-website">Website</label>
          <input
            id="f-website"
            name="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
          />
        </div>

        {turnstileSitekey && (
          <div className="fld c3">
            <Turnstile
              handvat={turnstile}
              onToken={(token) => {
                turnstileToken.current = token
              }}
            />
          </div>
        )}

        <div
          className={melding ? 'form-alert is-on' : 'form-alert'}
          id="formAlert"
          role="alert"
        >
          {melding}
        </div>

        {/*
          Het bedrag staat vóór de knop en niet erop (FR-09, ADR-0008). Het moet
          kenbaar zijn voordat de bestelling geplaatst wordt — art. 6:230v BW —
          en dat is hier ook waar het staat: pal boven de verzendknop, in dezelfde
          vorm als op de betaalpagina, zodat de cliënt hetzelfde bedrag herkent.

          Dit is een weergave; de prijs die geïncasseerd wordt komt van de server
          (WP-11), die een bedrag uit de POST domweg niet kent.
        */}
        <div className="paybox c6">
          <div>
            <b>GPK-beoordeling</b>
            <br />
            <span style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>
              U betaalt met iDEAL, direct na het verzenden
            </span>
          </div>
          <span className="amount">{tariefTekst}</span>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn--coral btn--big"
            disabled={bezig}
          >
            {bezig ? 'Een moment geduld…' : 'Vraag aan en betaal'}
          </button>
          {/*
            Ook hier beloofde de bestaande site een boekingsstap ("Daarna kiest u
            direct een afspraakmoment"). FR-13a wil op deze plek juist de
            toegezegde reactietermijn; die staat nog open als O-11 en wordt hier
            ingevuld zodra hij bekend is.
          */}
          <p className="note">
            Na het verzenden betaalt u veilig met iDEAL. Daarna nemen wij
            contact met u op om een afspraakmoment te plannen.
          </p>
        </div>
      </div>
    </form>
  )
}

/** `is-error` op de veldhouder; daar hangt de rode omlijning aan. */
function fldKlasse(kolommen: string, fout: string | undefined): string {
  return fout ? `fld ${kolommen} is-error` : `fld ${kolommen}`
}

function isFormulierveld(veld: string): veld is keyof AanvraagFormulierWaarden {
  return (veldvolgorde as readonly string[]).includes(veld)
}

function leesVeld(form: HTMLFormElement, naam: string): string {
  const waarde = new FormData(form).get(naam)
  return typeof waarde === 'string' ? waarde : ''
}

/**
 * Leest het formulier uit.
 *
 * Waarden worden getrimd, want een spatie voor een e-mailadres is een typefout
 * en geen invoer. De server trimt zelf ook bij het valideren; dit zorgt ervoor
 * dat wat er verstuurd wordt hetzelfde is als wat er gekeurd is.
 */
function leesWaarden(form: HTMLFormElement): AanvraagFormulierWaarden {
  const data = new FormData(form)

  const lees = (naam: string) => {
    const waarde = data.get(naam)
    return typeof waarde === 'string' ? waarde.trim() : ''
  }

  return {
    kaarttype: lees('kaarttype'),
    aanvraagtype: lees('aanvraagtype'),
    achternaam: lees('achternaam'),
    tussenvoegsel: lees('tussenvoegsel'),
    voorletters: lees('voorletters'),
    voornaam: lees('voornaam'),
    geboortedatum: lees('geboortedatum'),
    geslacht: lees('geslacht'),
    telefoon: lees('telefoon'),
    straat: lees('straat'),
    huisnummer: lees('huisnummer'),
    postcode: lees('postcode'),
    woonplaats: lees('woonplaats'),
    gemeente: lees('gemeente'),
    email: lees('email'),
  }
}
